/**
 * Cart Framing Dropdown
 * When a framing product and a frameable jersey are both in the cart, shows a
 * required dropdown on the framing line item and keeps it in sync across cart
 * page and cart drawer re-renders.
 */
(function () {
  'use strict';

  if (window.FrameworthCartFramingDropdown) {
    window.FrameworthCartFramingDropdown.refresh('script-reload');
    return;
  }

  var state = {
    isUpdating: false,
    isInitializing: false,
    refreshTimer: null,
    bodyObserver: null,
    patchedCartItems: false,
    patchedCartDrawer: false,
    metadataCleanupPromise: null,
    metadataCleanupStartedAt: 0,
    isPageUnloading: false
  };

  function removeJerseySelectionLines(note) {
    if (typeof note !== 'string' || !note) return '';

    return note
      .split(/\r?\n/)
      .filter(function (line) {
        return !/^Jersey Selection \([^)]*\): /.test(line.trim());
      })
      .join('\n')
      .trim();
  }

  function hasJerseySelectionAttribute(attributes) {
    return Object.prototype.hasOwnProperty.call(attributes, 'Jersey Selection');
  }

  function decodeHtmlEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) {
      return value;
    }

    var textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function getCartRoots() {
    return Array.prototype.slice.call(document.querySelectorAll('cart-items'));
  }

  function getSelectForRow(row) {
    var nextRow = row.nextElementSibling;
    if (nextRow && nextRow.classList.contains('framing-dropdown-row')) {
      return nextRow.querySelector('.framing-dropdown__select');
    }

    return row.querySelector('.framing-dropdown__select');
  }

  function findDropdownContainer(framingRow) {
    var nextRow = framingRow.nextElementSibling;
    var container = null;

    if (nextRow && nextRow.classList.contains('framing-dropdown-row')) {
      container = nextRow.querySelector('.framing-dropdown-container');
    }

    if (!container) {
      container = framingRow.querySelector('.framing-dropdown-container');
    }

    if (!container) {
      container = document.createElement('div');
      container.className = 'framing-dropdown-container';

      var lastTd = framingRow.querySelector('td:last-child');
      if (lastTd) {
        lastTd.appendChild(container);
      } else {
        framingRow.appendChild(container);
      }
    }

    return container;
  }

  function scanCart(root) {
    if (!root || root.dataset.empty === 'true') {
      return { framing: [], jerseys: [] };
    }

    var rows = root.querySelectorAll('.cart-item');
    var framing = [];
    var jerseys = [];

    rows.forEach(function (row) {
      if (row.dataset.isFraming === 'true') {
        framing.push(row);
        return;
      }

      if (row.dataset.isJersey === 'true') {
        jerseys.push(row);
        return;
      }

      var titleEl = row.querySelector('.js-item-name, .cart-item__title, a[href*="/products/"]');
      var title = (titleEl ? titleEl.textContent : '').trim().toLowerCase();

      if (title.includes('custom jersey framing') || title.includes('custom framing jersey')) {
        row.dataset.isFraming = 'true';
        row.dataset.itemTitle = titleEl ? titleEl.textContent.trim() : '';
        row.dataset.lineIndex = row.dataset.lineIndex || row.id.replace('cart-item-', '');
        framing.push(row);
      } else if (title.includes('jersey') || title.includes('signed')) {
        row.dataset.isJersey = 'true';
        row.dataset.itemTitle = titleEl ? titleEl.textContent.trim() : '';
        jerseys.push(row);
      }
    });

    return { framing: framing, jerseys: jerseys };
  }

  function truncateWords(str, maxWords) {
    var words = str.split(/\s+/);
    if (words.length <= maxWords) return str;
    return words.slice(0, maxWords).join(' ') + '...';
  }

  function getItemTitle(row) {
    var title = row.dataset.itemTitle || '';
    var variantTitle = row.dataset.variantTitle || '';

    if (variantTitle && variantTitle !== 'Default Title') {
      return title + ' - ' + variantTitle;
    }

    return title;
  }

  function getDisplayTitle(row) {
    return truncateWords(getItemTitle(row), 5);
  }

  function getExistingProperties(row) {
    var serialized = row.dataset.itemProperties || '{}';

    try {
      var raw = JSON.parse(serialized);

      if (Array.isArray(raw)) {
        var obj = {};
        raw.forEach(function (pair) {
          if (Array.isArray(pair) && pair.length === 2) {
            obj[pair[0]] = pair[1];
          }
        });
        return obj;
      }

      return raw;
    } catch (e) {
      try {
        var decoded = JSON.parse(decodeHtmlEntities(serialized));
        if (Array.isArray(decoded)) {
          var decodedObj = {};
          decoded.forEach(function (pair) {
            if (Array.isArray(pair) && pair.length === 2) {
              decodedObj[pair[0]] = pair[1];
            }
          });
          return decodedObj;
        }
        return decoded;
      } catch (err) {
        return {};
      }
    }
  }

  function setExistingProperties(row, properties) {
    row.dataset.itemProperties = JSON.stringify(properties);
  }

  function getLineIndex(row) {
    return parseInt(row.dataset.lineIndex, 10);
  }

  function getLineQuantity(row) {
    var qtyInput = row.querySelector('.qty-input__input');
    return qtyInput ? parseInt(qtyInput.value, 10) : 1;
  }

  function getCartChangeUrl() {
    return window.theme && theme.routes && theme.routes.cartChange
      ? theme.routes.cartChange
      : '/cart/change.js';
  }

  function parseResponseError(response) {
    return response
      .json()
      .then(function (data) {
        if (data && data.description) return data.description;
        if (data && data.message) return data.message;
        if (data && data.errors) return data.errors;
        return response.status;
      })
      .catch(function () {
        return response.status;
      });
  }

  function refreshCartViewForRow(row) {
    var cartRoot = row.closest('cart-items');

    if (cartRoot && typeof cartRoot.refresh === 'function') {
      cartRoot.refresh();
      return;
    }

    scheduleRefresh('property-save');
  }

  function saveJerseySelection(framingRow, value) {
    var properties = getExistingProperties(framingRow);

    if (value) {
      properties['Jersey Selection'] = value;
    } else {
      delete properties['Jersey Selection'];
    }

    var payload = {
      line: getLineIndex(framingRow),
      quantity: getLineQuantity(framingRow),
      properties: properties
    };

    return fetch(getCartChangeUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) {
        return parseResponseError(response).then(function (message) {
          throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
        });
      }

      return response.json();
    }).then(function (cart) {
      setExistingProperties(framingRow, properties);
      framingRow.dataset.jerseySelection = value || '';
      refreshCartViewForRow(framingRow);
      return cart;
    });
  }

  function clearJerseySelection(framingRow) {
    saveJerseySelection(framingRow, '').catch(function (err) {
      console.error('Failed to clear jersey selection:', err);
    });
  }

  function getSelectionLines(framingRows) {
    var lines = [];

    framingRows.forEach(function (row) {
      var select = getSelectForRow(row);
      if (select && select.value) {
        var framingTitle = truncateWords(row.dataset.itemTitle || 'Custom Framing', 5);
        lines.push('Jersey Selection (' + framingTitle + '): ' + select.value);
      }
    });

    return lines;
  }

  function normalizeCartAttributes(attributes) {
    if (!attributes || typeof attributes !== 'object') {
      return {};
    }

    if (Array.isArray(attributes)) {
      var normalized = {};
      attributes.forEach(function (pair) {
        if (Array.isArray(pair) && pair.length === 2) {
          normalized[pair[0]] = pair[1];
        }
      });
      return normalized;
    }

    return attributes;
  }

  var _persistentNote = '';

  function syncCartNoteTextarea(note) {
    _persistentNote = note;
    // Update any note textareas in the DOM so checkout form submissions don't clobber the saved note
    document.querySelectorAll('textarea[name="note"]').forEach(function (el) {
      el.value = note;
    });
  }

  // Keep the note textarea synced every 300ms — if anything wipes it, restore immediately
  setInterval(function () {
    if (!_persistentNote) return;
    document.querySelectorAll('textarea[name="note"]').forEach(function (el) {
      if (el.value !== _persistentNote) {
        el.value = _persistentNote;
      }
    });
  }, 300);

  function updateCartMetadataWithValue(framingRow, value) {
    if (!value) return Promise.resolve();
    var framingTitle = truncateWords(framingRow.dataset.itemTitle || 'Custom Framing', 5);
    var selectionLine = 'Jersey Selection (' + framingTitle + '): ' + value;

    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var existingNote = cart.note || '';
        var cleanedNote = removeJerseySelectionLines(existingNote);
        var newNote = cleanedNote ? cleanedNote + '\n' + selectionLine : selectionLine;

        var attributes = normalizeCartAttributes(cart.attributes);
        attributes['Jersey Selection'] = selectionLine;

        // Sync the textarea BEFORE the server update so form submissions are consistent
        syncCartNoteTextarea(newNote);

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note: newNote,
            attributes: attributes
          })
        }).then(function (res) {
          // Re-sync after server confirms to ensure DOM matches server state
          syncCartNoteTextarea(newNote);
          return res;
        });
      })
      .catch(function (err) {
        console.error('Failed to update cart metadata:', err);
      });
  }

  function updateCartMetadata(framingRows) {
    var lines = getSelectionLines(framingRows);
    var selectionValue = lines.join('\n');

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var existingNote = cart.note || '';
        var cleanedNote = removeJerseySelectionLines(existingNote);
        var newNote = selectionValue
          ? (cleanedNote ? cleanedNote + '\n' + selectionValue : selectionValue)
          : cleanedNote;

        var attributes = normalizeCartAttributes(cart.attributes);
        if (selectionValue) {
          attributes['Jersey Selection'] = selectionValue;
        } else {
          attributes['Jersey Selection'] = '';
        }

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note: newNote,
            attributes: attributes
          })
        });
      })
      .catch(function (err) {
        console.error('Failed to update cart metadata:', err);
      });
  }

  function beaconClearCartMetadataWithoutFraming() {
    var noteField = document.querySelector('textarea[name="note"]');
    var currentNote = noteField ? noteField.value : _persistentNote;
    var cleanedNote = removeJerseySelectionLines(currentNote);
    var shouldSendNote = cleanedNote !== (currentNote || '').trim();
    var payloadData = {
      attributes: { 'Jersey Selection': '' }
    };

    if (shouldSendNote) {
      payloadData.note = cleanedNote;
      syncCartNoteTextarea(cleanedNote);
    }

    if (!navigator.sendBeacon) return;

    var payload = new Blob(
      [JSON.stringify(payloadData)],
      { type: 'application/json' }
    );
    navigator.sendBeacon('/cart/update.js', payload);
  }

  function clearCartMetadataWithoutFraming() {
    if (state.isPageUnloading) {
      return Promise.resolve(null);
    }

    if (state.metadataCleanupPromise) {
      return state.metadataCleanupPromise;
    }

    if (Date.now() - state.metadataCleanupStartedAt < 2200) {
      return Promise.resolve(null);
    }

    state.metadataCleanupStartedAt = Date.now();
    state.metadataCleanupPromise = fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attributes = normalizeCartAttributes(cart.attributes);
        var cleanedNote = removeJerseySelectionLines(cart.note || '');
        var hadSelectionNote = cleanedNote !== (cart.note || '').trim();
        var hadSelectionAttribute = hasJerseySelectionAttribute(attributes);

        if (!hadSelectionNote && !hadSelectionAttribute) return null;

        attributes['Jersey Selection'] = '';
        syncCartNoteTextarea(cleanedNote);

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note: cleanedNote,
            attributes: attributes
          })
        });
      })
      .catch(function (err) {
        state.metadataCleanupStartedAt = 0;
        if (!state.isPageUnloading) {
          console.error('Failed to clear stale framing cart metadata:', err);
        }
        return null;
      })
      .finally(function () {
        state.metadataCleanupPromise = null;
      });

    return state.metadataCleanupPromise;
  }

  function enforceOneToOne(framingRows) {
    var selections = {};

    framingRows.forEach(function (row) {
      var select = getSelectForRow(row);
      if (select && select.value && select.value !== 'I will send my own jersey') {
        selections[row.dataset.lineIndex] = select.value;
      }
    });

    framingRows.forEach(function (row) {
      var select = getSelectForRow(row);
      if (!select) return;

      var thisLine = row.dataset.lineIndex;

      Array.prototype.slice.call(select.options).forEach(function (opt) {
        if (!opt.value || opt.value === 'I will send my own jersey') return;

        var takenByOther = false;
        for (var line in selections) {
          if (line !== thisLine && selections[line] === opt.value) {
            takenByOther = true;
            break;
          }
        }

        opt.disabled = takenByOther;
      });
    });
  }

  function updateCheckoutState(framingRows) {
    var allSelected = framingRows.length > 0 && framingRows.every(function (row) {
      var select = getSelectForRow(row);
      return select && select.value;
    });

    document.querySelectorAll('[name="checkout"]').forEach(function (btn) {
      var parent = btn.parentElement;
      if (!parent) return;

      var existingWarn = parent.querySelector('.framing-checkout-warning');

      if (!allSelected && framingRows.length > 0) {
        btn.disabled = true;
        btn.classList.add('btn--disabled');

        if (!existingWarn) {
          var warn = document.createElement('p');
          warn.className = 'framing-checkout-warning text-sm mt-2';
          warn.style.color = 'var(--color-error-text, #dc2626)';
          warn.style.textAlign = 'center';
          warn.textContent = 'Please select a jersey option for all framing products before checkout.';
          parent.appendChild(warn);
        }
      } else {
        btn.disabled = false;
        btn.classList.remove('btn--disabled');
        if (existingWarn) existingWarn.remove();
      }
    });
  }

  function handleSelection(select, framingRow, framingRows) {
    if (state.isUpdating) return;

    var previousValue = framingRow.dataset.jerseySelection || '';
    var nextValue = select.value;

    state.isUpdating = true;
    select.disabled = true;
    framingRow.dataset.jerseySelection = nextValue;
    enforceOneToOne(framingRows);
    updateCheckoutState(framingRows);

    // Sync textarea IMMEDIATELY so if customer clicks checkout before async save
    // completes, the form submission carries the right note value
    if (nextValue) {
      var framingTitle = truncateWords(framingRow.dataset.itemTitle || 'Custom Framing', 5);
      var immediateLine = 'Jersey Selection (' + framingTitle + '): ' + nextValue;
      syncCartNoteTextarea(immediateLine);
    }

    // Save note ONLY — skip line item property save because the section
    // re-render it triggers wipes our textarea sync, breaking checkout.
    updateCartMetadataWithValue(framingRow, nextValue)
      .catch(function () {})
      .finally(function () {
        // Re-sync textarea after fetch completes (in case anything reset it)
        if (nextValue) {
          var t = truncateWords(framingRow.dataset.itemTitle || 'Custom Framing', 5);
          syncCartNoteTextarea('Jersey Selection (' + t + '): ' + nextValue);
        }
        select.disabled = false;
        state.isUpdating = false;
      });
  }

  function buildDropdown(framingRow, jerseyItems, framingRows) {
    var container = findDropdownContainer(framingRow);
    var currentSelection = framingRow.dataset.jerseySelection || '';
    var lineIndex = framingRow.dataset.lineIndex;

    container.innerHTML = '';

    var wrapper = document.createElement('div');
    wrapper.className = 'framing-dropdown';

    var label = document.createElement('label');
    label.className = 'framing-dropdown__label';
    label.textContent = 'Select the jersey you are sending in for framing';
    label.setAttribute('for', 'framing-select-' + lineIndex);

    var select = document.createElement('select');
    select.className = 'framing-dropdown__select input';
    select.id = 'framing-select-' + lineIndex;
    select.dataset.lineIndex = lineIndex;

    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = '\u2014 Select an option \u2014';
    placeholder.disabled = true;
    placeholder.selected = !currentSelection;
    select.appendChild(placeholder);

    if (jerseyItems.length === 0) {
      var ownOnly = document.createElement('option');
      ownOnly.value = 'I will send my own jersey';
      ownOnly.textContent = 'I will send my own jersey';
      ownOnly.selected = currentSelection === 'I will send my own jersey';
      select.appendChild(ownOnly);
    } else if (jerseyItems.length === 1) {
      var jersey = jerseyItems[0];
      var title = getItemTitle(jersey);
      var displayTitle = getDisplayTitle(jersey);

      var singleOption = document.createElement('option');
      singleOption.value = title;
      singleOption.textContent = displayTitle;
      singleOption.dataset.jerseyVariantId = jersey.dataset.variantId;
      if (currentSelection === title) singleOption.selected = true;
      select.appendChild(singleOption);

      var sendOwnOption = document.createElement('option');
      sendOwnOption.value = 'I will send my own jersey';
      sendOwnOption.textContent = 'I will send my own jersey';
      if (currentSelection === 'I will send my own jersey') sendOwnOption.selected = true;
      select.appendChild(sendOwnOption);
    } else {
      jerseyItems.forEach(function (item) {
        var multiTitle = getItemTitle(item);
        var multiDisplayTitle = getDisplayTitle(item);
        var option = document.createElement('option');
        option.value = multiTitle;
        option.textContent = multiDisplayTitle;
        option.dataset.jerseyVariantId = item.dataset.variantId;
        if (currentSelection === multiTitle) option.selected = true;
        select.appendChild(option);
      });

      var fallbackOption = document.createElement('option');
      fallbackOption.value = 'I will send my own jersey';
      fallbackOption.textContent = 'I will send my own jersey';
      if (currentSelection === 'I will send my own jersey') fallbackOption.selected = true;
      select.appendChild(fallbackOption);
    }

    if (currentSelection && currentSelection !== 'I will send my own jersey') {
      var stillExists = jerseyItems.some(function (item) {
        return getItemTitle(item) === currentSelection;
      });

      if (!stillExists) {
        select.value = '';
        framingRow.dataset.jerseySelection = '';
        clearJerseySelection(framingRow);
      }
    }

    var warning = document.createElement('div');
    warning.className = 'framing-dropdown__warning';
    warning.textContent = 'Please select a jersey option before checkout';
    if (select.value) warning.hidden = true;

    select.addEventListener('change', function (event) {
      // Stop event from bubbling to cart-items handler which treats it as quantity change
      event.stopPropagation();
      handleSelection(event.target, framingRow, framingRows);
      warning.hidden = !!event.target.value;
    });

    wrapper.appendChild(label);
    wrapper.appendChild(select);
    wrapper.appendChild(warning);
    container.appendChild(wrapper);
  }

  function guardForms() {
    ['cart', 'cart-drawer-form'].forEach(function (formId) {
      var form = document.getElementById(formId);
      if (!form || form.dataset.framingGuard === 'true') return;

      form.dataset.framingGuard = 'true';
      form.addEventListener('submit', function (event) {
        var roots = getCartRoots();
        var framingRows = [];

        roots.forEach(function (root) {
          framingRows = framingRows.concat(scanCart(root).framing);
        });

        if (framingRows.length === 0) {
          beaconClearCartMetadataWithoutFraming();
          clearCartMetadataWithoutFraming();
          return;
        }

        for (var i = 0; i < framingRows.length; i += 1) {
          var select = getSelectForRow(framingRows[i]);
          if (!select || !select.value) {
            event.preventDefault();
            alert('Please select a jersey option for all framing products before checkout.');
            return;
          }
        }

        // Build the note from current selections
        var noteLines = [];
        framingRows.forEach(function (row) {
          var s = getSelectForRow(row);
          if (s && s.value) {
            var title = truncateWords(row.dataset.itemTitle || 'Custom Framing', 5);
            noteLines.push('Jersey Selection (' + title + '): ' + s.value);
          }
        });

        if (noteLines.length === 0) return;

        var fullNote = noteLines.join('\n');

        // Sync textarea + add hidden note input as safety net
        syncCartNoteTextarea(fullNote);

        // If form has no note input/textarea, add a hidden one
        var existingNoteField = form.querySelector('[name="note"]');
        if (!existingNoteField) {
          var hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'note';
          hidden.value = fullNote;
          form.appendChild(hidden);
        }

        // Fire-and-forget save via sendBeacon — guarantees server gets the note
        // even if the form submission races. sendBeacon sends in background during navigation.
        if (navigator.sendBeacon) {
          var payload = new Blob(
            [JSON.stringify({ note: fullNote })],
            { type: 'application/json' }
          );
          navigator.sendBeacon('/cart/update.js', payload);
        }
      });
    });
  }

  function cleanupWhenNoFraming() {
    document.querySelectorAll('.framing-checkout-warning').forEach(function (el) {
      el.remove();
    });

    document.querySelectorAll('[name="checkout"].btn--disabled').forEach(function (btn) {
      var parent = btn.parentElement;
      var warning = parent ? parent.querySelector('.framing-checkout-warning') : null;
      if (!warning) {
        btn.disabled = false;
        btn.classList.remove('btn--disabled');
      }
    });

    clearCartMetadataWithoutFraming();
  }

  function init() {
    state.isInitializing = true;

    var roots = getCartRoots();
    var framingRows = [];

    roots.forEach(function (root) {
      var cart = scanCart(root);

      cart.framing.forEach(function (row) {
        buildDropdown(row, cart.jerseys, cart.framing);
      });

      framingRows = framingRows.concat(cart.framing);
    });

    if (framingRows.length === 0) {
      cleanupWhenNoFraming();
      guardForms();
      state.isInitializing = false;
      return;
    }

    enforceOneToOne(framingRows);
    updateCheckoutState(framingRows);
    guardForms();
    state.isInitializing = false;
  }

  function scheduleRefresh(reason) {
    clearTimeout(state.refreshTimer);

    state.refreshTimer = setTimeout(function () {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          init();
        });
      });
    }, reason === 'mutation' ? 30 : 0);
  }

  function observeFallbackContainers() {
    if (!state.bodyObserver) {
      state.bodyObserver = new MutationObserver(function (mutations) {
        if (state.isUpdating || state.isInitializing) return;

        var SELECTOR = 'cart-items, cart-drawer, .cart-item, .framing-dropdown-row, .framing-dropdown-container';

        var shouldRefresh = mutations.some(function (mutation) {
          if (mutation.type !== 'childList') return false;

          return Array.prototype.slice.call(mutation.addedNodes).some(function (node) {
            return node.nodeType === 1 && (
              (node.matches && node.matches(SELECTOR)) ||
              (node.querySelector && node.querySelector(SELECTOR))
            );
          });
        });

        if (shouldRefresh) {
          scheduleRefresh('mutation');
        }
      });

      state.bodyObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  function patchMethod(target, methodName, callback) {
    if (!target || typeof target[methodName] !== 'function' || target[methodName]._framingPatched) return;

    var original = target[methodName];

    target[methodName] = async function () {
      try {
        return await original.apply(this, arguments);
      } finally {
        callback.call(this, methodName);
      }
    };

    target[methodName]._framingPatched = true;
  }

  function patchCartItems() {
    if (state.patchedCartItems) return;

    customElements.whenDefined('cart-items').then(function () {
      var ctor = customElements.get('cart-items');
      if (!ctor || !ctor.prototype) return;

      patchMethod(ctor.prototype, 'updateQuantity', function () {
        scheduleRefresh('cart-items:updateQuantity');
      });

      patchMethod(ctor.prototype, 'refresh', function () {
        scheduleRefresh('cart-items:refresh');
      });

      state.patchedCartItems = true;
      scheduleRefresh('cart-items:patched');
    });
  }

  function patchCartDrawer() {
    if (state.patchedCartDrawer) return;

    customElements.whenDefined('cart-drawer').then(function () {
      var ctor = customElements.get('cart-drawer');
      if (!ctor || !ctor.prototype) return;

      patchMethod(ctor.prototype, 'renderContents', function () {
        scheduleRefresh('cart-drawer:renderContents');
      });

      patchMethod(ctor.prototype, 'refresh', function () {
        scheduleRefresh('cart-drawer:refresh');
      });

      state.patchedCartDrawer = true;
      scheduleRefresh('cart-drawer:patched');
    });
  }

  // The cart drawer injects updated HTML on a delayed setTimeout (~700ms) after
  // an add, while on:cart:add fires immediately. A single refresh races ahead of
  // the render, so fire a short burst that covers the delayed re-render.
  function refreshBurst(reason) {
    scheduleRefresh(reason);
    [300, 750, 1200, 1800].forEach(function (delay) {
      setTimeout(function () { scheduleRefresh(reason + ':burst'); }, delay);
    });
  }

  function bindEvents() {
    [
      'on:cart:add',
      'on:line-item:change',
      'dispatch:cart-drawer:refresh',
      'dispatch:cart-drawer:open',
      'on:cart-drawer:after-open',
      'shopify:section:load',
      'cart:updated'
    ].forEach(function (eventName) {
      document.addEventListener(eventName, function () {
        refreshBurst(eventName);
      });
    });
  }

  function setup() {
    var markPageUnloading = function () {
      state.isPageUnloading = true;
    };
    window.addEventListener('beforeunload', markPageUnloading);
    window.addEventListener('pagehide', markPageUnloading);
    window.addEventListener('pageshow', function () {
      state.isPageUnloading = false;
    });

    bindEvents();
    patchCartItems();
    patchCartDrawer();
    observeFallbackContainers();
    scheduleRefresh('setup');
  }

  window.FrameworthCartFramingDropdown = {
    refresh: scheduleRefresh,
    init: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup, { once: true });
  } else {
    setup();
  }
})();
